import * as React from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import { OtaUpdatesWrapper } from "./style/otaUpdates";
import { OSTYPE_INVERSE, OTA_PLATFORM_SLUG, versionRegex } from "../../config/constant";
import API from "../../api/axios";
import { APP_URLS } from "../../api/url";
import { APIError } from "../../core/utils";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 560,
  maxWidth: "90%",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
};

const hasExtension = (file, ext) =>
  !!file && file.name.toLowerCase().endsWith(`.${ext}`);

export default function UploadRelease({ OSType, limits, onUploaded }) {
  const [open, setOpen] = React.useState(false);
  const [loading, _loading] = React.useState(false);
  const [runtimeVersion, _runtimeVersion] = React.useState("");
  const [manifest, _manifest] = React.useState(null);
  const [jsbundle, _jsbundle] = React.useState(null);

  const uploadSupported = limits ? limits.upload_supported : true;

  const handleOpen = () => {
    _runtimeVersion("");
    _manifest(null);
    _jsbundle(null);
    setOpen(true);
  };
  const handleClose = () => {
    if (!loading) setOpen(false);
  };

  const validate = () => {
    if (!versionRegex.test(runtimeVersion.trim())) {
      toast.error("Runtime version must look like 3.2.0.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    if (!manifest && !jsbundle) {
      toast.error("Select a manifest and/or a JS bundle.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    if (manifest && !hasExtension(manifest, "json")) {
      toast.error("Manifest must be a .json file.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    if (jsbundle && !hasExtension(jsbundle, "jsbundle")) {
      toast.error("JS bundle must be a .jsbundle file.", {
        position: toast.POSITION.TOP_RIGHT,
      });
      return false;
    }
    if (limits) {
      if (manifest && manifest.size > limits.max_manifest_kb * 1024) {
        toast.error(`Manifest exceeds ${limits.max_manifest_kb} KB.`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        return false;
      }
      if (jsbundle && jsbundle.size > limits.max_jsbundle_kb * 1024) {
        toast.error(`JS bundle exceeds ${Math.round(limits.max_jsbundle_kb / 1024)} MB.`, {
          position: toast.POSITION.TOP_RIGHT,
        });
        return false;
      }
    }
    return true;
  };

  const handleSubmit = () => {
    if (loading || !validate()) return;

    const partial = !manifest || !jsbundle;
    const version = runtimeVersion.trim();

    confirmAlert({
      title: "Publish OTA release",
      message: partial
        ? `You are uploading only the ${manifest ? "manifest" : "JS bundle"} for ${OSTYPE_INVERSE[OSType]} ${version}. The other file stays as it is. Every ${OSTYPE_INVERSE[OSType]} app on version ${version} will download this on its next launch. Continue?`
        : `Publish manifest + JS bundle for ${OSTYPE_INVERSE[OSType]} ${version}? Every ${OSTYPE_INVERSE[OSType]} app on version ${version} will download it on its next launch.`,
      closeOnEscape: true,
      buttons: [
        { label: "Yes, publish", onClick: () => handleSave(version) },
        { label: "No" },
      ],
      closeOnClickOutside: true,
      overlayClassName: "overlay-custom-class-name",
    });
  };

  const handleSave = (version) => {
    const formData = new FormData();
    formData.append("platform", OTA_PLATFORM_SLUG[OSType]);
    formData.append("runtime_version", version);
    if (manifest) formData.append("manifest", manifest);
    if (jsbundle) formData.append("jsbundle", jsbundle);

    _loading(true);
    API.post(APP_URLS.OTA_FILES_UPLOAD, formData)
      .then((res) => {
        const resp = res.data;
        _loading(false);
        if (resp.success === true) {
          toast.success(`OTA release ${version} published for ${OSTYPE_INVERSE[OSType]}.`, {
            position: toast.POSITION.TOP_RIGHT,
          });
          setOpen(false);
          onUploaded();
        } else {
          toast.error(resp.message, { position: toast.POSITION.TOP_RIGHT });
        }
      })
      .catch((error) => {
        _loading(false);
        // nginx / PHP reject oversized bodies before Laravel runs, so there is no JSON envelope.
        let msg;
        if (error?.response?.status === 413) {
          msg = "The upload is larger than this server accepts. Check the upload limits.";
        } else if (error?.response) {
          msg = APIError(error);
        } else {
          msg = error?.message || "Something went wrong!";
        }
        toast.error(msg, { position: toast.POSITION.TOP_RIGHT });
      });
  };

  return (
    <>
      <button
        className="btn"
        onClick={handleOpen}
        disabled={!uploadSupported}
        title={uploadSupported ? "" : "Server upload limits are too small for a release"}
      >
        + Upload release
      </button>
      <Modal open={open} onClose={handleClose} sx={{ zIndex: 20 }}>
        <Box sx={style}>
          <OtaUpdatesWrapper>
            <h2 className="header">Upload OTA release</h2>
            <div className="form-group">
              <label htmlFor="ota-platform">Platform</label>
              <input id="ota-platform" type="text" value={OSTYPE_INVERSE[OSType]} disabled />
            </div>
            <div className="form-group">
              <label htmlFor="ota-runtime-version">Runtime version</label>
              <input
                id="ota-runtime-version"
                type="text"
                placeholder="e.g. 3.2.0"
                value={runtimeVersion}
                onChange={(e) => _runtimeVersion(e.target.value)}
              />
            </div>
            <div className="form-hint">
              The native app version this bundle was built for. Only devices on exactly this
              version will receive it.
            </div>
            <div className="form-group">
              <label htmlFor="ota-manifest">Manifest (.json)</label>
              <input
                id="ota-manifest"
                type="file"
                accept=".json"
                onChange={(e) => _manifest(e.target.files?.[0] || null)}
              />
            </div>
            <div className="form-group">
              <label htmlFor="ota-jsbundle">JS bundle (.jsbundle)</label>
              <input
                id="ota-jsbundle"
                type="file"
                accept=".jsbundle"
                onChange={(e) => _jsbundle(e.target.files?.[0] || null)}
              />
            </div>
            <div className="form-hint">
              Send both files for a normal release. If a version is already live, its files are
              archived and replaced.
            </div>
            <div className="buttons">
              <button type="button" className="btn btn-secondary" disabled={loading} onClick={handleClose}>
                Cancel
              </button>
              <button className="btn btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? "Uploading…" : "Publish"}
              </button>
            </div>
          </OtaUpdatesWrapper>
        </Box>
      </Modal>
    </>
  );
}
